import { DoctorType, PatientDto, QueryFactory } from '@lmt/shared-types';
import {
  CustomDictionaryName,
  useDictionaryByTitle,
  useDictionaryValuesById,
  useDictionaryValuesByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import { CardData } from '@lmt/unicorn-shared/app/components/card-reader/components/CardReaderHandler';
import { RadioButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButton';
import {
  fieldFilter,
  isEmpty,
  titleFieldKey,
} from '@lmt/unicorn-shared/app/components/merge-assistant/form/components/common';
import { getFormattedDate } from '@lmt/unicorn-shared/app/components/patients/common';
import { getFullName } from '@lmt/unicorn-shared/app/components/patients/patient-form/common/common';
import { localizeSexType } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { snakeCase } from 'lodash';
import React, { FC } from 'react';
import styles from '@lmt/unicorn-shared/src/app/components/card-reader/components/PatientDataItem.module.scss';
import { useTranslation } from 'react-i18next';

const filter = [
  ...fieldFilter,
  'deletionReason',
  'emailsAllowed',
  'isPrivateInsurance',
  'isSelfPayer',
  'anonymous',
  'isDeceased',
  'houseNumber',
  'street',
  'healthInsuranceCompanyShortName',
  'healthInsuranceCompanyNumber',
  'healthInsuranceCompany',
  'titleAsString',
];

type Props = {
  patientId?: number;
  isSelected: boolean;
  selectPatient?: (patientId: number) => void;
  cardData?: CardData;
};
export const PatientDataItem: FC<Props> = (props) => {
  const { patientId, isSelected, selectPatient, cardData } = props;
  const i18Merge = useScopedTranslation('merge_assistant.merge_pair');
  const i18n = useTranslation();

  const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(
    patientId!,
    {
      enabled: !!patientId,
    },
  );
  const patient = patientQuery.data;

  const doctorsQuery = QueryFactory.DoctorQuery.useGetDoctorsQuery({
    all: true,
    doctorType: DoctorType.External,
  });

  const insuranceCompanyDictionary = useDictionaryByTitle(
    CustomDictionaryName.InsuranceCompanies,
  );
  const insuranceCompaniesQuery = useDictionaryValuesById(
    insuranceCompanyDictionary?.id,
  );
  const titleDictQuery = useDictionaryValuesByTitle(
    CustomDictionaryName.PersonTitles,
  );

  function getValue(fieldName: string, value: any) {
    if (isEmpty(value)) {
      return '-';
    }
    if (fieldName === 'dateOfBirth' || fieldName === 'dateOfDeath') {
      return getFormattedDate(value);
    } else if (fieldName === 'sex') {
      return localizeSexType(value);
    } else if (fieldName === 'healthInsuranceCompanyId') {
      const company = (insuranceCompaniesQuery.data ?? []).find(
        (company) => company.id === value,
      );
      return company?.value;
    } else if (fieldName === 'title') {
      if (!Number.isNaN(Number(value))) {
        const title = (titleDictQuery.data?.data ?? []).find(
          (title) => title.id === value,
        );
        return i18n.t(title?.value as any, { ns: 'data-block-fields' });
      }
    } else if (fieldName === 'doctorId') {
      const doctor = (doctorsQuery.data?.data ?? []).find(
        (doctor) => doctor.id === value,
      );
      return doctor?.name;
    }
    return value.toString() ?? '-';
  }

  const patientData = patient
    ? { ...patient, title: patient.values[titleFieldKey] }
    : cardData;

  if (!patientData) {
    return null;
  }

  return (
    <div
      className={styles.container}
      data-is-selected={isSelected}
      data-is-selectable={!!patientId}
      onClick={() => {
        if (patientId && selectPatient) {
          selectPatient(patientId);
        }
      }}
      data-test-id="patient-data-card-container"
    >
      <div
        className={styles.patientHeader}
        data-test-id="patient-card-data-header"
      >
        {patientId && (
          <RadioButton
            name={`select_patient_${patientId}`}
            checked={isSelected}
            className={styles.patientName}
          />
        )}
        <div className={styles.patientName} data-test-id="patient-full-name">
          {getFullName(patientData)}
        </div>
      </div>
      <div className={styles.patientFields} data-test-id="patient-fields">
        {Object.entries(patientData)
          .filter(([key, value]) => !!value && !filter.includes(key))
          .map(([key, value]) => {
            const displayValue = getValue(key, value);
            return (
              <div className={styles.row} data-test-id="patient-field-row">
                <div
                  className={styles.fieldTitle}
                  data-test-id="patient-field-title"
                >
                  {i18Merge.t(`fields.${snakeCase(key)}` as any, {
                    defaultValue: key,
                  })}
                </div>
                <div
                  className={styles.value}
                  title={displayValue}
                  data-test-id="patient-field-value"
                >
                  {displayValue}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
